/** The volume autopilot: what it is doing this minute and why, the rest of
 *  today with and without it, what it has learned about each kind of text,
 *  and its settings. See backend/app/engine/autopilot.py. */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Columns, Legend, LineChart, LoadMeter, Sparkline, StackBar } from "../components/charts";
import { Card, ErrorBanner, Pill, fmt } from "../components/ui";
import {
  api,
  type AircallStatus,
  type AutopilotConfig,
  type AutopilotModel,
  type AutopilotPlan,
  type AutopilotSim,
  type AutopilotStatus,
  type Me,
  type Segment,
  type Template,
} from "../lib/api";

const clock = (iso: string | null | undefined) =>
  iso
    ? new Date(iso).toLocaleTimeString("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit" })
    : "—";
const one = (v: number | null | undefined) => (v == null ? "—" : (Math.round(v * 10) / 10).toLocaleString("en-US"));
const pct = (v: number) => `${(v * 100).toFixed(v < 0.01 ? 2 : 1)}%`;

/** What the autopilot is doing - a state, so it wears the status colours. */
const MODES: Record<string, { label: string; tone: string; color: string; says: string }> = {
  room: { label: "Room to spare", tone: "ok", color: "var(--ok)", says: "Everything due is going; the team could take more." },
  metering: { label: "Metering", tone: "wa", color: "var(--warn)", says: "The team is full: texts that make work wait for room." },
  catch_up: { label: "Catching up", tone: "ba", color: "var(--bad)", says: "Sending faster than the team's room to finish the day." },
  off_shift: { label: "Nobody on shift", tone: "nu", color: "var(--ink3)", says: "Only reminders, waiting new sign-ups and cheap texts go." },
};
/** Queue lanes - categories, so they take the categorical series in order. */
const LANES: Array<{ key: string; label: string; color: string }> = [
  { key: "must", label: "Always go", color: "var(--series-1)" },
  { key: "metered", label: "Make work - wait for room", color: "var(--series-2)" },
  { key: "standby", label: "Standby blasts", color: "var(--series-3)" },
  { key: "cheap", label: "Cheap - full speed", color: "var(--series-4)" },
];
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function Autopilot({ me }: { me: Me }) {
  // the autopilot role exists to run this page (backend app/routes/auth.py)
  const canChange = me.role === "admin" || me.role === "manager" || me.role === "autopilot";
  const [st, setSt] = useState<AutopilotStatus | null>(null);
  const [plan, setPlan] = useState<AutopilotPlan | null>(null);
  const [model, setModel] = useState<AutopilotModel | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [whatIf, setWhatIf] = useState<{ agents: string; perAgent: string }>({ agents: "", perAgent: "" });
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);

  const load = useCallback(() => {
    if (inFlight.current) return;
    inFlight.current = true;
    api
      .autopilot()
      .then((s) => {
        setSt(s);
        setError(null);
      })
      .catch((e) => setError(e.message))
      .finally(() => {
        inFlight.current = false;
      });
  }, []);

  const loadPlan = useCallback(() => {
    const a = whatIf.agents.trim() === "" ? null : Number(whatIf.agents);
    const p = whatIf.perAgent.trim() === "" ? null : Number(whatIf.perAgent);
    api
      .autopilotPlan(a, p)
      .then(setPlan)
      .catch((e) => setError(e.message));
  }, [whatIf]);

  useEffect(() => {
    load();
    api.autopilotModel().then(setModel).catch((e) => setError(e.message));
    const id = setInterval(() => {
      if (!document.hidden) load();
    }, 20000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    loadPlan();
    const id = setInterval(() => {
      if (!document.hidden) loadPlan();
    }, 60000);
    return () => clearInterval(id);
  }, [loadPlan]);

  if (!st)
    return (
      <>
        <ErrorBanner error={error} />
        {!error && <div className="empty">Reading the autopilot…</div>}
      </>
    );

  const c = st.config;
  const d = st.decision;
  const mode = d && d.current ? MODES[d.mode] : null;
  const refresh = () => {
    load();
    loadPlan();
  };
  const toggle = () => {
    setBusy(true);
    api
      .updateAutopilot({ enabled: !c.enabled })
      .then(refresh)
      .catch((e) => setError(e.message))
      .finally(() => setBusy(false));
  };
  const recent = st.recent.slice(-60);

  return (
    <>
      <ErrorBanner error={error} />

      <div className="row w" style={{ gap: 10 }}>
        <div className="dim" style={{ fontSize: 13.5 }}>
          Sends as fast as the people on the phones can take the conversations it starts - no faster, no slower.
        </div>
        <div className="sp" />
        {st.kill_switch && <Pill tone="ba">Kill switch on - nothing is sending</Pill>}
      </div>

      {/* ── status ── */}
      <div className="card ap-hero">
        <div>
          <div className="row w" style={{ gap: 12, justifyContent: "space-between" }}>
            <div className="ap-state" style={{ color: c.enabled && mode ? mode.color : "var(--ink3)" }}>
              <span className="dot" />
              <h2 style={{ color: "var(--ink)" }}>
                {c.enabled ? (mode ? mode.label : "On") : "Off"}
              </h2>
              {!c.enabled && mode && <Pill tone="nu">would be: {mode.label}</Pill>}
            </div>
            <button
              className={`ap-toggle${c.enabled ? " on" : ""}`}
              disabled={!canChange || busy || (!c.enabled && !c.has_schedule)}
              onClick={toggle}
              title={!c.has_schedule ? "Set the shift pattern first" : undefined}
            >
              <span className="sw" />
              {c.enabled ? "On" : "Turn on"}
            </button>
          </div>
          <p className="ap-reason">
            {!c.has_schedule
              ? "Set the agents' shift pattern below to see what it would do. Until then - and while it is off - texts go at the fixed sending speed."
              : d
                ? d.reason
                : "Working out this minute…"}
          </p>
          {d && (
            <div className="ap-eq">
              <span className="t">team can take <b>{one(d.detail.team_can_take)}</b></span>
              <span className="op">+</span>
              <span className="t">keep ready <b>{one(d.detail.keep_waiting)}</b></span>
              <span className="op">−</span>
              <span className="t">waiting <b>{one(d.detail.waiting)}</b></span>
              <span className="op">−</span>
              <span className="t">on the way <b>{one(d.detail.coming)}</b></span>
              <span className="op">=</span>
              <span className="t" style={{ borderColor: "var(--acc-line)" }}>room <b>{one(d.detail.room)}</b></span>
              <span className="dim" style={{ fontSize: 11.5 }}>· next {d.detail.lookahead} min · {clock(d.minute)}</span>
            </div>
          )}
          {!c.enabled && (
            <div className="dim" style={{ fontSize: 12, marginTop: 14 }}>
              Off: texts go at the fixed sending speed of {st.sending_speed} a minute. The sending speed stays the
              ceiling when it is on; reminders always go; new sign-ups never wait more than {c.hot_max_wait_minutes} min.
            </div>
          )}
        </div>
        <div>
          <div className="lab" style={{ marginBottom: 10 }}>The next {d?.detail.lookahead ?? 60} minutes</div>
          {d ? (
            <LoadMeter waiting={d.detail.waiting} coming={d.detail.coming} capacity={d.detail.team_can_take} />
          ) : (
            <div className="dim">No agents on the shift pattern yet.</div>
          )}
          <div className="lab" style={{ margin: "20px 0 8px" }}>How is the floor?</div>
          <PulseControl st={st} canChange={canChange} onDone={load} onError={setError} />
          <LiveTeam st={st} />
          <QuietDay st={st} canChange={canChange} onDone={load} onError={setError} />
        </div>
      </div>

      {/* ── figures ── */}
      <div className="tiles">
        <div className="tile">
          <span className="lab">On the phones now{st.live_team?.used ? " (Aircall)" : ""}</span>
          <span className="v">{one(st.live_team?.used ? st.live_team.on_shift : st.agents_now)}</span>
          <span className="d">
            can take ~{one(st.live_team?.used
              ? st.live_team.on_shift * st.config.conversations_per_agent_hour
              : st.capacity_per_hour)} conversations an hour
          </span>
        </div>
        <div className={`tile ${d && d.detail.waiting > (d.detail.keep_waiting || 0) * 2 ? "ba" : ""}`}>
          <span className="lab">Conversations waiting</span>
          <span className="v">{d ? one(d.detail.waiting) : "—"}</span>
          <Sparkline values={recent.map((r) => r.waiting ?? 0)} color="var(--series-2)" />
          <span className="d">
            {d?.detail.measured_waiting != null
              ? `measured in GHL · ${Math.round((d.detail.measured_share_unanswered ?? 0) * 100)}% of ${d.detail.measured_read} unanswered`
              : "estimated from today's replies"}
          </span>
        </div>
        <div className="tile">
          <span className="lab">On the way, next hour</span>
          <span className="v">{d ? one(d.detail.next_hour_expected ?? d.detail.coming) : "—"}</span>
          <span className="d">replies expected from texts already sent</span>
        </div>
        <div className="tile">
          <span className="lab">Texts a minute</span>
          <span className="v">{recent.length ? fmt.n(recent[recent.length - 1].texts) : "—"}</span>
          <Sparkline values={recent.map((r) => r.texts)} />
          <span className="d">ceiling {st.sending_speed} · last hour</span>
        </div>
        <div className="tile">
          <span className="lab">Model vs today</span>
          <span className="v">{d?.detail.calibration_raw != null ? `×${d.detail.calibration_raw.toFixed(2)}` : "—"}</span>
          <span className="d">
            {d?.detail.predicted_recent != null
              ? `${one(d.detail.actual_recent)} replies in 90 min, ${one(d.detail.predicted_recent)} expected`
              : "today's replies against the model"}
          </span>
        </div>
      </div>

      <PlanCard plan={plan} whatIf={whatIf} setWhatIf={setWhatIf} enabled={c.enabled} />

      <div className="g2">
        <Card title="Due now">
          <StackBar
            parts={LANES.map((l) => {
              const due = st.due_now.find((x) => x.lane === l.key);
              return {
                label: l.label,
                value: due?.texts ?? 0,
                color: l.color,
                detail: due ? `${one(due.conversations)} conversations' worth` : undefined,
              };
            })}
          />
        </Card>
        <RecentCard st={st} />
      </div>

      <ModelCard model={model} canChange={canChange} onLearned={() => api.autopilotModel().then(setModel)} onError={setError} />
      <SettingsCard cfg={c} canChange={canChange} onSaved={refresh} onError={setError} />
    </>
  );
}

// ── the rest of today ─────────────────────────────────────────
function PlanCard({
  plan,
  whatIf,
  setWhatIf,
  enabled,
}: {
  plan: AutopilotPlan | null;
  whatIf: { agents: string; perAgent: string };
  setWhatIf: (w: { agents: string; perAgent: string }) => void;
  enabled: boolean;
}) {
  const [agents, setAgents] = useState(whatIf.agents);
  const [perAgent, setPerAgent] = useState(whatIf.perAgent);
  return (
    <Card
      title={
        <div>
          <h3>The rest of today</h3>
          <div className="dim" style={{ fontSize: 12, marginTop: 1 }}>
            With the autopilot against the fixed sending speed, simulated minute by minute from the real queue
          </div>
        </div>
      }
      actions={
        <div className="row" style={{ gap: 6, fontSize: 12.5 }}>
          <span className="dim">What if</span>
          <input className="f" style={{ width: 80 }} type="number" min={0} placeholder="agents"
                 value={agents} onChange={(e) => setAgents(e.target.value)} />
          <span className="dim">×</span>
          <input className="f" style={{ width: 80 }} type="number" min={1} placeholder="per hr"
                 value={perAgent} onChange={(e) => setPerAgent(e.target.value)} />
          <button className="btn s" onClick={() => setWhatIf({ agents, perAgent })}>Show</button>
          {(whatIf.agents || whatIf.perAgent) && (
            <button className="btn s" onClick={() => { setAgents(""); setPerAgent(""); setWhatIf({ agents: "", perAgent: "" }); }}>
              Reset
            </button>
          )}
        </div>
      }
      footer={
        <span className="dim">
          Conversations are expected numbers from the learned reply rates; the team's capacity is the shift
          pattern × conversations per agent. {enabled ? "" : "The autopilot is off: today is actually going at the fixed speed."}
        </span>
      }
    >
      {!plan ? (
        <div className="empty">Working out the rest of the day…</div>
      ) : !plan.sending_day ? (
        <div className="empty">No more sending today.</div>
      ) : !plan.autopilot || !plan.fixed ? (
        <div className="empty">Set the shift pattern to see the plan.</div>
      ) : (
        <PlanBody plan={plan} />
      )}
    </Card>
  );
}

function PlanBody({ plan }: { plan: AutopilotPlan }) {
  const a = plan.autopilot as AutopilotSim;
  const f = plan.fixed as AutopilotSim;
  const rows = useMemo(() => {
    const byAt = new Map<string, { at: string; auto?: AutopilotSim["buckets"][0]; fixed?: AutopilotSim["buckets"][0] }>();
    for (const b of a.buckets) byAt.set(b.at, { at: b.at, auto: b });
    for (const b of f.buckets) byAt.set(b.at, { ...(byAt.get(b.at) ?? { at: b.at }), fixed: b });
    return [...byAt.values()].sort((x, y) => x.at.localeCompare(y.at));
  }, [a, f]);
  const x = rows.map((r) => r.at);
  const compare = (label: string, auto: number, fixed: number, unit = "") => {
    const better = auto < fixed;
    const same = Math.abs(auto - fixed) < 0.05;
    return (
      <div className="c">
        <div className="lab" style={{ marginBottom: 4 }}>{label}</div>
        <div className="n">
          {one(auto)}
          {unit}
          {!same && <span className={`ap-delta ${better ? "good" : "bad"}`}>{better ? "better" : "worse"}</span>}
        </div>
        <div className="vs">fixed {plan.fixed_rate}/min: {one(fixed)}{unit}</div>
      </div>
    );
  };
  return (
    <div className="col" style={{ gap: 18 }}>
      <div className="ap-cmp">
        {a.on_the_phone != null && (
          <div className="c" style={{ background: "var(--acc-soft)", borderColor: "var(--acc-line)" }}>
            <div className="lab" style={{ marginBottom: 4 }}>Team on the phone</div>
            <div className="n">{Math.round(a.on_the_phone * 100)}%</div>
            <div className="vs">
              {plan.standby && plan.standby.more_needed > 0
                ? `a standby blast of ~${fmt.n(plan.standby.more_needed)} would fill the rest`
                : `fixed speed: ${f.on_the_phone != null ? Math.round(f.on_the_phone * 100) : "—"}%`}
            </div>
          </div>
        )}
        {compare("Nobody reaches", a.never_reached, f.never_reached)}
        {compare("Average wait", a.average_wait_minutes, f.average_wait_minutes, " min")}
        {compare("Most waiting at once", a.peak_waiting, f.peak_waiting)}
        <div className="c">
          <div className="lab" style={{ marginBottom: 4 }}>Texts to go</div>
          <div className="n">{fmt.n(plan.texts_to_go)}</div>
          <div className="vs">
            {a.texts_left ? `${fmt.n(a.texts_left)} carried to tomorrow` : "all go today"}
            {a.started_early ? ` · ${fmt.n(a.started_early)} early` : ""}
          </div>
        </div>
      </div>
      <div>
        <div className="row w" style={{ marginBottom: 8 }}>
          <b style={{ fontSize: 13 }}>Conversations starting, per 15 minutes</b>
          <div className="sp" />
          <Legend
            items={[
              { label: "With the autopilot", color: "var(--series-1)" },
              { label: `Fixed ${plan.fixed_rate}/min`, color: "var(--series-2)" },
              { label: "What the team can take", color: "var(--series-neutral)" },
            ]}
          />
        </div>
        <LineChart
          x={x}
          now={plan.as_of}
          series={[
            { key: "cap", label: "Team can take", color: "var(--series-neutral)", values: rows.map((r) => r.auto?.team_can_take ?? null), step: true, quiet: true },
            { key: "fixed", label: `Fixed ${plan.fixed_rate}/min`, color: "var(--series-2)", values: rows.map((r) => r.fixed?.conversations ?? null) },
            { key: "auto", label: "With the autopilot", color: "var(--series-1)", values: rows.map((r) => r.auto?.conversations ?? null), area: true },
          ]}
        />
      </div>
      <div>
        <div className="row w" style={{ marginBottom: 8 }}>
          <b style={{ fontSize: 13 }}>Conversations waiting for someone</b>
          <div className="sp" />
          <Legend
            items={[
              { label: "With the autopilot", color: "var(--series-1)" },
              { label: `Fixed ${plan.fixed_rate}/min`, color: "var(--series-2)" },
            ]}
          />
        </div>
        <LineChart
          x={x}
          now={plan.as_of}
          height={150}
          series={[
            { key: "fixed", label: `Fixed ${plan.fixed_rate}/min`, color: "var(--series-2)", values: rows.map((r) => r.fixed?.waiting ?? null) },
            { key: "auto", label: "With the autopilot", color: "var(--series-1)", values: rows.map((r) => r.auto?.waiting ?? null), area: true },
          ]}
        />
      </div>
    </div>
  );
}

// ── the last hour ─────────────────────────────────────────────
function RecentCard({ st }: { st: AutopilotStatus }) {
  const rows = st.recent.slice(-60);
  return (
    <Card title="Minute by minute, the last hour">
      {rows.length === 0 ? (
        <div className="empty" style={{ padding: 16 }}>No decisions yet - they start the first minute it is on.</div>
      ) : (
        <>
          <Columns
            items={rows.map((r) => ({
              key: r.minute,
              value: r.texts,
              color: MODES[r.mode]?.color ?? "var(--ink3)",
              tip: (
                <>
                  <div className="viz-tip-h">{clock(r.minute)} · {MODES[r.mode]?.label ?? r.mode}</div>
                  <div className="viz-tip-r"><b>{r.texts}</b><span>texts</span></div>
                  <div className="viz-tip-r"><b>{one(r.conversations_sent)}</b><span>conversations' worth</span></div>
                  <div className="viz-tip-r"><b>{one(r.waiting)}</b><span>waiting</span></div>
                  {r.started_early ? <div className="viz-tip-r"><b>{r.started_early}</b><span>started early</span></div> : null}
                </>
              ),
            }))}
          />
          <div style={{ marginTop: 10 }}>
            <Legend items={Object.values(MODES).map((m) => ({ label: m.label, color: m.color, kind: "box" as const }))} />
          </div>
        </>
      )}
    </Card>
  );
}

// ── what it has learned ───────────────────────────────────────
function ModelCard({
  model,
  canChange,
  onLearned,
  onError,
}: {
  model: AutopilotModel | null;
  canChange: boolean;
  onLearned: () => void;
  onError: (e: string) => void;
}) {
  const [q, setQ] = useState("");
  const [all, setAll] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!model) return null;
  const rows = model.rates
    .filter((r) => !r.key.startsWith("t:") && r.key !== "k:campaign")
    .filter((r) => !q || r.label.toLowerCase().includes(q.toLowerCase()));
  const shown = all ? rows : rows.slice(0, 15);
  const top = Math.max(0.0001, ...rows.map((r) => r.rate));
  const fams: Array<{ key: string; label: string; color: string }> = [
    { key: "hot", label: "New sign-ups", color: "var(--series-1)" },
    { key: "drip", label: "Drips", color: "var(--series-2)" },
    { key: "blast", label: "Blasts", color: "var(--series-3)" },
    { key: "reminder", label: "Reminders", color: "var(--series-4)" },
  ];
  return (
    <Card
      title={
        <div>
          <h3>What it has learned</h3>
          <div className="dim" style={{ fontSize: 12, marginTop: 1 }}>
            {fmt.n(model.meta.texts)} texts over {model.meta.days ?? 15} days · re-learned every half hour
            {model.learned_at ? ` · last ${clock(model.learned_at)}` : ""}
          </div>
        </div>
      }
      actions={
        <div className="row" style={{ gap: 6 }}>
          <input className="f" style={{ width: 210 }} placeholder="Find a campaign or day" value={q} onChange={(e) => setQ(e.target.value)} />
          {canChange && (
            <button
              className="btn s"
              disabled={busy}
              onClick={() => {
                setBusy(true);
                api.autopilotLearn().then(onLearned).catch((e) => onError(e.message)).finally(() => setBusy(false));
              }}
            >
              {busy ? "Learning…" : "Re-learn now"}
            </button>
          )}
        </div>
      }
      footer={
        <span className="dim">
          A conversation is a reply within four hours, before our next text - opt-outs excluded. Days with few texts
          are pulled toward their wider group, so one lucky reply cannot make a day look hot.
          {model.defaults_in_use && " Nothing learned yet - using the measured defaults."}
        </span>
      }
    >
      <div className="sidebar" style={{ gridTemplateColumns: "minmax(0, 1fr) 300px" }}>
        <div>
          <div className="tw">
            <table>
              <thead>
                <tr>
                  <th>Kind of text</th>
                  <th style={{ textAlign: "right" }}>Texts</th>
                  <th style={{ textAlign: "right" }}>Conversations</th>
                  <th>Reply rate used</th>
                  <th style={{ textAlign: "right" }}>1 in</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r.key}>
                    <td style={{ fontWeight: 560 }}>{r.label}</td>
                    <td style={{ textAlign: "right" }} className="num">{fmt.n(r.texts)}</td>
                    <td style={{ textAlign: "right" }} className="num">{fmt.n(r.conversations)}</td>
                    <td>
                      <div className="ap-rate" title={`measured ${pct(r.raw)}, used ${pct(r.rate)}`}>
                        <div className="bar" style={{ width: `${Math.max(2, (r.rate / top) * 110)}px` }} />
                        <span className="num" style={{ fontSize: 12 }}>{pct(r.rate)}</span>
                      </div>
                    </td>
                    <td style={{ textAlign: "right" }} className="num dim">{r.rate > 0 ? fmt.n(Math.round(1 / r.rate)) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {rows.length > 15 && (
            <button className="btn s" style={{ marginTop: 10 }} onClick={() => setAll(!all)}>
              {all ? "Show fewer" : `Show all ${rows.length}`}
            </button>
          )}
        </div>
        <div>
          <b style={{ fontSize: 13 }}>How fast replies come</b>
          <div className="dim" style={{ fontSize: 12, margin: "2px 0 12px" }}>share of a text's replies in, by minutes after it</div>
          <div className="tw">
            <table>
              <thead>
                <tr>
                  <th></th>
                  <th style={{ textAlign: "right" }}>15 min</th>
                  <th style={{ textAlign: "right" }}>1 hour</th>
                </tr>
              </thead>
              <tbody>
                {fams.map((fm) => {
                  const cv = model.reply_timing[fm.key] ?? {};
                  return (
                    <tr key={fm.key}>
                      <td><span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 3, background: fm.color, marginRight: 7 }} />{fm.label}</td>
                      <td style={{ textAlign: "right" }} className="num">{Math.round((cv["15"] ?? 0) * 100)}%</td>
                      <td style={{ textAlign: "right" }} className="num">{Math.round((cv["60"] ?? 0) * 100)}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Card>
  );
}

// ── the floor's word ──────────────────────────────────────────
function PulseControl({
  st,
  canChange,
  onDone,
  onError,
}: {
  st: AutopilotStatus;
  canChange: boolean;
  onDone: () => void;
  onError: (e: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const press = (level: "behind" | "idle" | "clear") => {
    setBusy(true);
    api.autopilotPulse(level).then(onDone).catch((e) => onError(e.message)).finally(() => setBusy(false));
  };
  const p = st.team_pulse_now;
  const tc = st.team_check;
  return (
    <div className="col" style={{ gap: 8 }}>
      <div className="seg-ctl">
        <button className={p < 1 ? "on" : ""} disabled={!canChange || busy} onClick={() => press("behind")}>We're behind</button>
        <button className={p === 1 ? "on" : ""} disabled={!canChange || busy} onClick={() => press("clear")}>As planned</button>
        <button className={p > 1 ? "on" : ""} disabled={!canChange || busy} onClick={() => press("idle")}>Waiting for work</button>
      </div>
      <div className="dim" style={{ fontSize: 12 }}>
        {p < 1
          ? `Team taken as ${Math.round(p * 100)}% speed until ${clock(st.config.team_pulse_until)}.`
          : p > 1
            ? `Team taken as ${Math.round(p * 100)}% speed until ${clock(st.config.team_pulse_until)}.`
            : "One press adjusts the next hour - whoever sees the real queue in GHL knows first."}
        {tc && (
          <> GHL check {clock(tc.at)}: {tc.unanswered} of {tc.read} unanswered
            {tc.median_answer_minutes != null ? `, answered in ~${Math.round(tc.median_answer_minutes)} min` : ""}
            {tc.used ? " (in use)." : "."}</>
        )}
      </div>
    </div>
  );
}

// ── the team, from Aircall ────────────────────────────────────
const REASON: Record<string, string> = {
  on_a_break: "on a break", doing_back_office: "back office", in_training: "in training",
  out_for_lunch: "at lunch", other: "other",
};

function LiveTeam({ st }: { st: AutopilotStatus }) {
  const lt = st.live_team;
  if (!lt && !st.config.live_team) return null;
  const n = (k: AircallStatus) => lt?.counts?.[k] ?? 0;
  const away = lt?.reasons
    ? Object.entries(lt.reasons).filter(([, v]) => v > 0).map(([k, v]) => `${v} ${REASON[k] ?? k.replace(/_/g, " ")}`)
    : [];
  return (
    <div className="col" style={{ gap: 6, marginTop: 14 }}>
      <div className="row" style={{ gap: 8, alignItems: "center" }}>
        <span className="lab">Aircall</span>
        {!lt ? (
          <Pill tone="wa">no reading yet</Pill>
        ) : lt.error ? (
          <Pill tone="ba">not readable - using the shift pattern</Pill>
        ) : !lt.fresh ? (
          <Pill tone="wa">stale - using the shift pattern</Pill>
        ) : lt.used ? (
          <Pill tone="ok">in use</Pill>
        ) : (
          <Pill tone="nu">watching only</Pill>
        )}
        {lt && <span className="dim" style={{ fontSize: 12 }}>{clock(lt.at)}</span>}
      </div>
      {lt && !lt.error && (
        <div style={{ fontSize: 13 }}>
          <b>{one(lt.on_shift)}</b> of {lt.members} on shift
          <span className="dim"> (pattern {one(st.agents_now)})</span>
          {st.decision?.detail.live_turnout != null && (
            <span className="dim">
              {" "}· today {Math.round((st.decision.detail.live_turnout as number) * 100)}% of the pattern, so the
              rest of the day is planned on that
            </span>
          )}
          <div className="dim" style={{ fontSize: 12 }}>
            {n("available")} available · {n("in_call")} in a call · {n("after_call_work")} wrapping up ·{" "}
            {n("do_not_disturb")} do not disturb · {n("offline")} offline
            {away.length > 0 && <> - away: {away.join(", ")}</>}
          </div>
        </div>
      )}
      {lt?.error && <div className="dim" style={{ fontSize: 12 }}>{lt.error}</div>}
    </div>
  );
}

// ── running out of work ───────────────────────────────────────
function QuietDay({ st, canChange, onDone, onError }: {
  st: AutopilotStatus;
  canChange: boolean;
  onDone: () => void;
  onError: (e: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const topUp = () => {
    setBusy(true);
    api
      .autopilotTopUp({ preview: true })
      .then((look) => {
        if (look.off || look.skipped) {
          throw new Error(
            typeof look.off === "string"
              ? look.off
              : look.skipped ?? "Switch quiet-day cover on and choose an audience first.",
          );
        }
        const ok = window.confirm(
          `Send ${(look.texts ?? 0).toLocaleString()} texts now from the audience in Settings.\n\n` +
            `About ${Math.round(look.expected_conversations ?? 0)} conversations and ` +
            `${Math.round(look.expected_calls ?? 0)} calls, on this hour's rate.\n` +
            `They go behind every drip, reminder and ordinary blast.\n` +
            `${(look.cap_left ?? 0).toLocaleString()} left of today's cap.`,
        );
        if (!ok) return null;
        return api.autopilotTopUp({});
      })
      .then((sent) => { if (sent) onDone(); })
      .catch((e) => onError(e.message))
      .finally(() => setBusy(false));
  };
  const k = st.keep_busy;
  if (!k || k.no_schedule || k.error) return null;
  const idle = k.idle_conversations ?? 0;
  if (!k.on && idle < 1) return null;
  return (
    <div className="col" style={{ gap: 4, marginTop: 14 }}>
      <div className="row" style={{ gap: 8, alignItems: "center" }}>
        <span className="lab">Running out of work</span>
        {k.on ? <Pill tone="ok">cover on</Pill> : <Pill tone="nu">cover off</Pill>}
      </div>
      <div style={{ fontSize: 13 }}>
        {idle < 1 ? (
          <>The rest of today keeps the team busy.</>
        ) : (
          <>
            <b>{one(idle)}</b> conversations of today&apos;s capacity look like going unused
            <span className="dim">
              {" "}· about {fmt.n(k.texts_needed ?? 0)} more texts would fill it
              {k.on && <> · {fmt.n(k.sent_today ?? 0)} sent, {fmt.n(k.cap_left ?? 0)} left of today&apos;s cap</>}
            </span>
          </>
        )}
      </div>
      {canChange && (
        <div>
          <button className="btn" disabled={busy} onClick={topUp}>
            {busy ? "Working…" : "Volume is low — send a top-up"}
          </button>
        </div>
      )}
    </div>
  );
}

// ── settings ──────────────────────────────────────────────────
function emptySchedule(): Record<string, number[]> {
  const s: Record<string, number[]> = {};
  for (let d = 1; d <= 7; d++) s[String(d)] = Array(24).fill(0);
  return s;
}

function SettingsCard({
  cfg,
  canChange,
  onSaved,
  onError,
}: {
  cfg: AutopilotConfig;
  canChange: boolean;
  onSaved: () => void;
  onError: (e: string) => void;
}) {
  const [draft, setDraft] = useState<AutopilotConfig>(cfg);
  const [saving, setSaving] = useState(false);
  // for quiet-day cover: the audiences and messages already approved
  const [segments, setSegments] = useState<Segment[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  useEffect(() => {
    api.segments().then(setSegments).catch(() => setSegments([]));
    api.templates().then((t) => setTemplates(t.filter((x) => x.status === "active")))
      .catch(() => setTemplates([]));
  }, []);
  const [allHours, setAllHours] = useState(false);
  const [fill, setFill] = useState({ agents: "8", from: "11", to: "21", days: "1,2,3,4,5,6" });
  const [today, setToday] = useState("");
  useEffect(() => setDraft(cfg), [cfg]);

  const sched = { ...emptySchedule(), ...(draft.agent_schedule || {}) };
  const hours = allHours ? [...Array(24).keys()] : [...Array(14).keys()].map((i) => i + 8);
  const peak = Math.max(1, ...Object.values(sched).flat());
  const set = <K extends keyof AutopilotConfig>(k: K, v: AutopilotConfig[K]) => setDraft({ ...draft, [k]: v });
  const setCell = (day: string, h: number, v: number) => {
    const next = { ...sched, [day]: [...sched[day]] };
    next[day][h] = Math.max(0, v || 0);
    set("agent_schedule", next);
  };
  const dirty = JSON.stringify({ ...draft, updated_at: null }) !== JSON.stringify({ ...cfg, updated_at: null });

  const save = (extra: Partial<AutopilotConfig> & { clear_override?: boolean } = {}) => {
    setSaving(true);
    const {
      has_schedule: _h, updated_at: _u, agents_override: _o, agents_override_until: _ou,
      team_pulse: _p, team_pulse_until: _pu, ...body
    } = { ...draft, ...extra };
    api
      .updateAutopilot({ ...body, agent_schedule: sched, ...extra })
      .then(onSaved)
      .catch((e) => onError(e.message))
      .finally(() => setSaving(false));
  };

  const field = (label: string, help: string, control: React.ReactNode, unit?: string) => (
    <div className="ap-field">
      <div className="l">
        <b>{label}</b>
        <span>{help}</span>
      </div>
      <div className="c">
        {control}
        <span className="u">{unit ?? ""}</span>
      </div>
    </div>
  );
  const num = (k: keyof AutopilotConfig, props: { min?: number; max?: number; step?: number } = {}) => (
    <input
      className="f"
      type="number"
      disabled={!canChange}
      min={props.min}
      max={props.max}
      step={props.step ?? 1}
      value={String(draft[k] ?? "")}
      onChange={(e) => set(k, Number(e.target.value) as never)}
    />
  );

  return (
    <Card
      title={
        <div>
          <h3>Settings</h3>
          <div className="dim" style={{ fontSize: 12, marginTop: 1 }}>Take effect within a minute. Every change goes in the Activity log.</div>
        </div>
      }
    >
      <div className="ap-sec">
        <div className="h">
          <h4>The team</h4>
          <p>Who is on the phones, and how many conversations each of them works through. The rest follows from these.</p>
        </div>
        <div className="col" style={{ gap: 14 }}>
          <div className="row w" style={{ gap: 8, fontSize: 13 }}>
            {cfg.agents_override != null ? (
              <>
                <Pill tone="ac">Today: {cfg.agents_override} agents until {clock(cfg.agents_override_until)}</Pill>
                {canChange && <button className="btn s" onClick={() => save({ clear_override: true })}>Back to the pattern</button>}
              </>
            ) : (
              <>
                <span>Today we have</span>
                <input className="f" type="number" min={0} style={{ width: 76 }} disabled={!canChange} value={today}
                       onChange={(e) => setToday(e.target.value)} placeholder="N" />
                <span>agents on the phones</span>
                <button className="btn s" disabled={!canChange || today === ""} onClick={() => save({ agents_override: Number(today) })}>
                  Set until tonight
                </button>
              </>
            )}
          </div>

          <div>
            <div className="row w" style={{ gap: 8, marginBottom: 8 }}>
              <b style={{ fontSize: 13 }}>Shift pattern</b>
              <span className="dim" style={{ fontSize: 12 }}>agents each hour, Eastern</span>
              <div className="sp" />
              <button className="btn s" onClick={() => setAllHours(!allHours)}>{allHours ? "8 AM – 10 PM" : "All 24 hours"}</button>
            </div>
            {canChange && (
              <div className="crow" style={{ marginBottom: 10, fontSize: 12.5 }}>
                <span>Fill</span>
                <input className="f" style={{ width: 64 }} type="number" min={0} value={fill.agents} onChange={(e) => setFill({ ...fill, agents: e.target.value })} />
                <span>agents from</span>
                <input className="f" style={{ width: 64 }} type="number" min={0} max={23} value={fill.from} onChange={(e) => setFill({ ...fill, from: e.target.value })} />
                <span>to</span>
                <input className="f" style={{ width: 64 }} type="number" min={1} max={24} value={fill.to} onChange={(e) => setFill({ ...fill, to: e.target.value })} />
                <select className="f" value={fill.days} onChange={(e) => setFill({ ...fill, days: e.target.value })}>
                  <option value="1,2,3,4,5,6">Mon – Sat</option>
                  <option value="1,2,3,4,5">Mon – Fri</option>
                  <option value="6">Saturday</option>
                  <option value="1,2,3,4,5,6,7">Every day</option>
                </select>
                <button
                  className="btn s"
                  onClick={() => {
                    const next = { ...sched };
                    for (const dd of fill.days.split(",")) {
                      next[dd] = [...next[dd]];
                      for (let h = Number(fill.from); h < Number(fill.to); h++) next[dd][h] = Number(fill.agents);
                    }
                    set("agent_schedule", next);
                  }}
                >
                  Apply
                </button>
              </div>
            )}
            <div style={{ overflowX: "auto" }}>
              <table className="ap-heat">
                <thead>
                  <tr>
                    <th></th>
                    {hours.map((h) => (
                      <th key={h}>{h === 0 ? "12a" : h < 12 ? `${h}a` : h === 12 ? "12p" : `${h - 12}p`}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {DAYS.map((name, i) => {
                    const day = String(i + 1);
                    return (
                      <tr key={day}>
                        <td className="day">{name}</td>
                        {hours.map((h) => {
                          const v = sched[day][h] || 0;
                          const share = v / peak;
                          return (
                            <td key={h}>
                              <input
                                type="number"
                                min={0}
                                disabled={!canChange}
                                value={v || ""}
                                placeholder="·"
                                style={{
                                  background: v ? `color-mix(in srgb, var(--series-1) ${Math.round(18 + share * 62)}%, var(--surf))` : "var(--surf2)",
                                  color: share > 0.55 ? "#fff" : "var(--ink)",
                                }}
                                onChange={(e) => setCell(day, h, Number(e.target.value))}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          {field("Conversations one agent takes an hour", "Read, answer, and the call that follows. The number that matters most - set it from what the team really does.", num("conversations_per_agent_hour", { min: 1, max: 120, step: 0.5 }), "/ hour")}
        </div>
      </div>

      <div className="ap-sec">
        <div className="h">
          <h4>Pacing</h4>
          <p>How full to keep the team. Busier means someone is always ready when a call ends - and a longer wait for the next reply.</p>
        </div>
        <div>
          {field("Fill this much of the team's time", "100 keeps everyone busy; below 100 leaves slack for a burst of replies.", num("target_utilization", { min: 10, max: 150 }), "%")}
          {field("Keep this many waiting per agent", "2 ≈ 97% of the shift on the phone, ~15 min average wait. 1 ≈ 95%, ~10 min.", num("waiting_per_agent", { min: 0, max: 20, step: 0.5 }))}
          {field("Look ahead", "Most replies come within the hour.", num("lookahead_minutes", { min: 15, max: 180 }), "min")}
        </div>
      </div>

      <div className="ap-sec">
        <div className="h">
          <h4>Rules</h4>
          <p>What always goes, what can move, and what never needs holding.</p>
        </div>
        <div>
          {field("A new sign-up waits at most", "Their Day 0 texts (and a new customer's welcome) then go whatever the load.", num("hot_max_wait_minutes", { min: 0, max: 240 }), "min")}
          {field("Start drip steps early, up to", "With room to spare: a step due at 1 PM may go at 11:30. Same day only, never before the opening. 0 = never.", num("pull_forward_minutes", { min: 0, max: 480 }), "min")}
          {field(
            "A text is cheap below",
            `Texts this unlikely to get a reply (1 in ${draft.cheap_below > 0 ? fmt.n(Math.round(1 / draft.cheap_below)) : "∞"}) are never held.`,
            <input className="f" type="number" disabled={!canChange} min={0} max={50} step={0.1}
                   value={Math.round(draft.cheap_below * 1000) / 10}
                   onChange={(e) => set("cheap_below", Number(e.target.value) / 100)} />,
            "%",
          )}
          {field("However full the team, still send", "Texts that make work. 0 = hold them all until there is room.", num("min_per_minute", { min: 0, max: 300 }), "/ min")}
        </div>
      </div>

      <div className="ap-sec">
        <div className="h">
          <h4>End of the day</h4>
          <p>When the day's texts will not fit the team. A reply nobody reaches is a lost lead; a text a day late is not.</p>
        </div>
        <div>
          <div className="ap-field">
            <div className="l">
              <b>When the day does not fit</b>
              <span>
                {draft.overflow === "balance"
                  ? "Finish today as far as the team can take it, and carry the rest to tomorrow's opening, first in line."
                  : draft.overflow === "finish"
                    ? "Everything due today goes by the finish time, however long the queue gets."
                    : "Never past the team's room; what does not fit goes tomorrow."}
              </span>
            </div>
            <div className="c" style={{ justifyContent: "flex-end" }}>
              <div className="seg-ctl">
                {(["balance", "finish", "hold"] as const).map((o) => (
                  <button key={o} className={draft.overflow === o ? "on" : ""} disabled={!canChange} onClick={() => set("overflow", o)}>
                    {o === "balance" ? "Balance" : o === "finish" ? "Finish" : "Hold"}
                  </button>
                ))}
              </div>
            </div>
          </div>
          {draft.overflow === "balance" &&
            field("Catching up may leave waiting, per agent", "Past this, what is left waits for tomorrow's opening.", num("overload_per_agent", { min: 0, max: 50, step: 0.5 }))}
          {draft.overflow !== "hold" && (
            <div className="ap-field">
              <div className="l">
                <b>Finish by</b>
                <span>Automatic: half an hour before the last staffed hour ends, never after the 8 PM close.</span>
              </div>
              <div className="c">
                <select className="f" disabled={!canChange} value={draft.finish_by === "auto" ? "auto" : "time"}
                        onChange={(e) => set("finish_by", e.target.value === "auto" ? "auto" : "19:00")}>
                  <option value="auto">Automatic</option>
                  <option value="time">At a time</option>
                </select>
                {draft.finish_by !== "auto" && (
                  <input className="f" disabled={!canChange} style={{ width: 76 }} value={draft.finish_by} onChange={(e) => set("finish_by", e.target.value)} />
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="ap-sec">
        <div className="h">
          <h4>Measuring the real queue</h4>
          <p>Optional. Off until its first readings have been checked against GHL.</p>
        </div>
        <div>
          <div className="ap-field">
            <div className="l">
              <b>Check the real queue in GHL</b>
              <span>Every 5 minutes, reads 12 recent conversations for a call, a note or a text from a person, and uses the share still unanswered instead of the estimate. About 5 GHL requests a minute.</span>
            </div>
            <div className="c">
              <button
                className={`ap-toggle${draft.measure_team ? " on" : ""}`}
                disabled={!canChange}
                onClick={() => set("measure_team", !draft.measure_team)}
              >
                <span className="sw" />
                {draft.measure_team ? "On" : "Off"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="ap-sec">
        <div className="h">
          <h4>Quiet-day cover</h4>
          <p>When the team is about to run out of work, release from one audience you approve here. Sent as standby: only into room left over after everything else.</p>
        </div>
        <div>
          <div className="ap-field">
            <div className="l">
              <b>Keep the phones busy</b>
              <span>Off: a quiet afternoon stays quiet unless someone queues a blast.</span>
            </div>
            <div className="c">
              <button
                className={`ap-toggle${draft.keep_busy ? " on" : ""}`}
                disabled={!canChange}
                onClick={() => set("keep_busy", !draft.keep_busy)}
              >
                <span className="sw" />
                {draft.keep_busy ? "On" : "Off"}
              </button>
            </div>
          </div>
          <div className="ap-field">
            <div className="l">
              <b>Audience</b>
              <span>A saved audience. Customers, people with an appointment, anyone texted recently or already queued are excluded anyway.</span>
            </div>
            <div className="c">
              <select className="f" disabled={!canChange} value={draft.keep_busy_segment_id ?? ""}
                      onChange={(e) => set("keep_busy_segment_id", e.target.value ? Number(e.target.value) : null)}>
                <option value="">— none —</option>
                {segments.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>
          <div className="ap-field">
            <div className="l">
              <b>Message</b>
              <span>The template it sends. Nothing is written automatically.</span>
            </div>
            <div className="c">
              <select className="f" disabled={!canChange} value={draft.keep_busy_template_id ?? ""}
                      onChange={(e) => set("keep_busy_template_id", e.target.value ? Number(e.target.value) : null)}>
                <option value="">— none —</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          </div>
          <div className="ap-field">
            <div className="l">
              <b>Most it may send in a day</b>
              <span>A hard stop, whatever the gap.</span>
            </div>
            <div className="c">
              <input className="f" type="number" min={0} style={{ width: 110 }}
                     disabled={!canChange} value={draft.keep_busy_daily_cap ?? 0}
                     onChange={(e) => set("keep_busy_daily_cap", Number(e.target.value))} />
            </div>
          </div>
          <div className="ap-field">
            <div className="l">
              <b>Act when this much would go unused</b>
              <span>Conversations of the next hour&apos;s capacity. Smaller acts sooner.</span>
            </div>
            <div className="c">
              <input className="f" type="number" min={0} step={1} style={{ width: 110 }}
                     disabled={!canChange} value={draft.keep_busy_min_idle ?? 3}
                     onChange={(e) => set("keep_busy_min_idle", Number(e.target.value))} />
            </div>
          </div>
        </div>
      </div>

      <div className="ap-sec">
        <div className="h">
          <h4>Who is on the phones</h4>
          <p>Read from Aircall every minute. Off: the shift pattern above is taken at its word.</p>
        </div>
        <div>
          <div className="ap-field">
            <div className="l">
              <b>Count the team from Aircall</b>
              <span>Available, in a call and wrapping up count as on shift; do not disturb (a break, back office, training) and offline do not. Later in the hour it moves back toward the pattern. If Aircall cannot be read, the pattern is used. One Aircall request a minute.</span>
            </div>
            <div className="c">
              <button
                className={`ap-toggle${draft.live_team ? " on" : ""}`}
                disabled={!canChange}
                onClick={() => set("live_team", !draft.live_team)}
              >
                <span className="sw" />
                {draft.live_team ? "On" : "Off"}
              </button>
            </div>
          </div>
          <div className="ap-field">
            <div className="l">
              <b>Aircall teams</b>
              <span>Whose status counts, comma-separated.</span>
            </div>
            <div className="c">
              <input
                className="f"
                value={draft.live_team_names ?? "Sales"}
                disabled={!canChange}
                onChange={(e) => set("live_team_names", e.target.value)}
                style={{ width: 200 }}
              />
            </div>
          </div>
        </div>
      </div>

      {canChange && dirty && (
        <div className="ap-savebar">
          <Pill tone="wa">Unsaved changes</Pill>
          <span className="dim" style={{ fontSize: 12.5 }}>Nothing changes until you save.</span>
          <div className="sp" />
          <button className="btn" onClick={() => setDraft(cfg)} disabled={saving}>Discard</button>
          <button className="btn p" onClick={() => save()} disabled={saving}>{saving ? "Saving…" : "Save changes"}</button>
        </div>
      )}
    </Card>
  );
}
